import React, { useState, useEffect } from 'react';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Mail, Save, TestTube } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminEmailConfig = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [config, setConfig] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Cimonamia'
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/settings/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setConfig({
          smtp_host: response.data.smtp_host || 'smtp.gmail.com',
          smtp_port: response.data.smtp_port || '587',
          smtp_user: response.data.smtp_user || '',
          smtp_password: response.data.smtp_password || '',
          smtp_from_email: response.data.smtp_from_email || '',
          smtp_from_name: response.data.smtp_from_name || 'Cimonamia'
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`${API}/settings/smtp`, {
        ...config,
        smtp_enabled: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Settings Saved',
        description: 'SMTP configuration has been updated successfully.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: 'Email Required',
        description: 'Please enter an email address to send test email',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API}/settings/smtp/test`, 
        { test_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast({
        title: 'Test Email Sent',
        description: `Test email sent to ${testEmail}. Check your inbox!`
      });
    } catch (error) {
      toast({
        title: 'Test Failed',
        description: error.response?.data?.detail || 'Failed to send test email',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-[#be1f1a]" />
        <div>
          <h2 className="text-2xl font-bold text-[#2C2420]">Email Configuration</h2>
          <p className="text-gray-600">Configure SMTP settings for order confirmations and notifications</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">📧 SMTP Setup Guide</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• For Gmail: Use App Password (not your regular password)</li>
          <li>• Enable 2-Factor Authentication in your Google Account</li>
          <li>• Generate App Password: Google Account → Security → 2-Step Verification → App Passwords</li>
          <li>• For other providers: Check their SMTP documentation</li>
        </ul>
      </div>

      <div className="grid gap-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="smtp_host">SMTP Host</Label>
            <Input
              id="smtp_host"
              value={config.smtp_host}
              onChange={(e) => setConfig({...config, smtp_host: e.target.value})}
              placeholder="smtp.gmail.com"
            />
          </div>

          <div>
            <Label htmlFor="smtp_port">SMTP Port</Label>
            <Input
              id="smtp_port"
              type="number"
              value={config.smtp_port}
              onChange={(e) => setConfig({...config, smtp_port: e.target.value})}
              placeholder="587"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="smtp_user">SMTP Username (Email)</Label>
          <Input
            id="smtp_user"
            type="email"
            value={config.smtp_user}
            onChange={(e) => setConfig({...config, smtp_user: e.target.value})}
            placeholder="your-email@gmail.com"
          />
        </div>

        <div>
          <Label htmlFor="smtp_password">SMTP Password (App Password for Gmail)</Label>
          <Input
            id="smtp_password"
            type="password"
            value={config.smtp_password}
            onChange={(e) => setConfig({...config, smtp_password: e.target.value})}
            placeholder="••••••••••••••••"
          />
        </div>

        <div>
          <Label htmlFor="smtp_from_email">From Email Address</Label>
          <Input
            id="smtp_from_email"
            type="email"
            value={config.smtp_from_email}
            onChange={(e) => setConfig({...config, smtp_from_email: e.target.value})}
            placeholder="noreply@cimonamia.com"
          />
        </div>

        <div>
          <Label htmlFor="smtp_from_name">From Name</Label>
          <Input
            id="smtp_from_name"
            value={config.smtp_from_name}
            onChange={(e) => setConfig({...config, smtp_from_name: e.target.value})}
            placeholder="Cimonamia"
          />
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="font-semibold mb-4">Test Email Configuration</h3>
        <div className="flex gap-4">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email to send test"
            className="flex-1"
          />
          <Button
            onClick={handleTestEmail}
            disabled={loading}
            variant="outline"
          >
            <TestTube className="h-4 w-4 mr-2" />
            Send Test
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-[#be1f1a] hover:bg-[#a71b17]"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default AdminEmailConfig;
